import { mutateVehicles } from "@/api/vehicle/useVehicleQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { VehicleCreateForm } from "@/features/vehicle/form/createForm";
import { handleErrorResponse } from "@/utils/errorMessage";
import styled from "@emotion/styled";
import { Button, Flex, Select, SelectProps, Text } from "@mantine/core";
import { ScmApiVehiclesPostRequest } from "@sizlcorp/sizl-api-document/dist/models";
import { IconX } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EventHandler, ReactNode, forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import customAlert from "../../alert/alert";
import { VehicleOption, useVehiclesAutoComplete, useVehiclesCode } from "./auto-vehicleGetQuery";

interface VehicleProps extends React.ComponentPropsWithoutRef<"div"> {
  group: string;
  label: string;
  value: string;
}

export interface VehicleAutoCompleteProps
  extends Partial<SelectProps>,
    Partial<React.RefAttributes<HTMLInputElement>> {
  value?: string | null;
  onChange?: (itemCode: string | null) => void;
  maxDropdownHeight?: number;
  width?: string;
}

export const VehicleAutoComplete = (params: VehicleAutoCompleteProps) => {
  const { value: vehicleCode, onChange, maxDropdownHeight, width, ...etcParams } = params;
  const [focused, setFocused] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const { t } = useTranslation();
  const { data: options } = useVehiclesAutoComplete(focused, searchKeyword);

  const { data: initialOptions } = useVehiclesCode(!!vehicleCode, vehicleCode ?? null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>("");
  let selectedRoute = initialOptions?.find((item) => {
    return item.value === vehicleCode;
  });

  const onChangeHandler = (e: string | null) => {
    selectedRoute = options?.find((item) => item.value === e);
    onChange && onChange(e);
  };

  const SelectItem = forwardRef<HTMLDivElement, VehicleProps>(
    ({ label: name, value: code, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Flex direction="row" justify="space-between" align="center">
          <Flex align={"center"}>
            <Text>{code}</Text>
            <Text fz="xs"> (차량 명: {name === "null" ? "-" : name})</Text>
          </Flex>
        </Flex>
      </div>
    ),
  );

  return (
    <VehicleSelect
      onDropdownOpen={() => setFocused(true)}
      onDropdownClose={() => setFocused(false)}
      clearable
      inputContainer={(children: ReactNode) => (
        <div style={{ position: "relative" }}>
          {children}
          {vehicleCode && (
            <div
              style={{
                position: "absolute",
                bottom: "0px",
                fontSize: "0.8em",
                color: "#888",
                left: "10px",
              }}
            >
              {"code: " + vehicleCode}
            </div>
          )}
        </div>
      )}
      value={vehicleCode}
      itemComponent={SelectItem}
      data={[...(options ?? []), ...(initialOptions ?? [])].reduce(
        (unique: VehicleProps[], option: VehicleProps) => {
          return unique.some((u) => u.value === option.value) ? unique : [...unique, option];
        },
        [],
      )}
      searchable
      maxDropdownHeight={maxDropdownHeight ?? 150}
      onChange={onChangeHandler}
      onSearchChange={(input) => {
        const regex = /^[A-Za-z0-9_]*$/;
        if (regex.test(input)) {
          setSearchKeyword(input);
          setErrorMessage(undefined);
        } else {
          setErrorMessage(t("message.noKorean"));
        }
      }}
      error={errorMessage}
      rightSection={VehicleInfo({
        route: selectedRoute as VehicleOption,
        onChange: onChangeHandler,
      })}
      filter={(value, item) =>
        item?.label?.toLowerCase().includes(value.toLowerCase().trim()) ||
        item?.value?.toLowerCase().includes(value.toLowerCase().trim())
      }
      nothingFound={AddNewVehicle({
        vehicleCode: searchKeyword,
        onChange: onChangeHandler,
      })}
      {...etcParams}
    />
  );
};

const VehicleInfo = (params: {
  route?: VehicleOption;
  onChange: (vehicleCode: string | null) => void;
}) => {
  const { route, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(null);
  };

  return route?.value ? (
    <VehicleInfoLabel>
      {/* ({route.value})  */}
      <IconX size="1rem" onClick={clearHandler} />
    </VehicleInfoLabel>
  ) : null;
};

const AddNewVehicle = (params: { vehicleCode: string; onChange: (itemCode: string) => void }) => {
  const { vehicleCode, onChange } = params;
  const { openModal, closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { mutate: postMutate } = useMutation(
    (params: ScmApiVehiclesPostRequest) =>
      mutateVehicles.create(params).mutationFn(params as ScmApiVehiclesPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["vehicles"]);
        customAlert(
          t("message.createSuccess", { item: t("vehicle.vehicle") }),
          t("common.createSuccess"),
          "green",
        );
        closeModal({});
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("vehicle.code") }),
          t("common.createFail", { item: t("vehicle.vehicle") }),
          "red",
        );
      },
    },
  );

  const onCloseHandler: EventHandler<any> = (values) => {
    postMutate({
      vehiclesGetRequest: {
        code: values.code!,
        name: values.name!,
        plateNumber: values.plateNumber!,
        driver: values.driver,
        description: values.description,
      },
    });
  };
  const addNewModalHandler: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    openModal(
      <VehicleCreateForm code={vehicleCode} autoCompleteOnSubmit={onCloseHandler} />,
      null,
      t("common.newCreate", { item: t("vehicle.vehicle") }),
      true,
    ).then((result) => {
      onChange(result.code);
    });
  };

  return (
    <Button variant="subtle" onClick={addNewModalHandler}>
      {t("common.newCreate", { item: `${t("vehicle.vehicle")} ${vehicleCode}` })}
    </Button>
  );
};
const VehicleInfoLabel = styled.div`
  font-size: 12px;
  color: #666666;
  // padding-right: 8px;
  padding-left: 0.4rem;
  display: flex;
  align-items: center;
  position: absolute;
`;

const VehicleSelect = styled(Select)<{ value: string | null | undefined }>`
  .mantine-Select-input {
    font-size: 0.8rem;
    padding-bottom: ${(props) => (props.value ? "0.8rem !important" : "")};

    &.item-dirty {
      color: cadetblue !important;
      border-color: cadetblue;
    }
  }
  .mantine-Select-error {
    font-size: 11px;
    color: red;
  }
`;
